/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * TIER ADJUSTMENT (v1_388).
 *
 * LOCATION. Under src/ rather than api/ for the same reason as interview-scope:
 * Create React App rejects imports that leave src/, and the dashboard needs
 * this so the screen and the exported record cannot disagree (v1_392).
 *
 * The methodology governs, and by v1_387 it governed a great deal: the tier,
 * the due diligence level, the review cadence, the source of wealth
 * requirement, which questions reach the client, and how long the interview
 * lasts. What it did not have was a way for the officer to disagree with it.
 *
 * That inverts the platform's own doctrine. Analysis proposes and the officer
 * disposes; an engine whose output no human can vary is the analysis disposing.
 * A risk methodology that cannot be departed from is also not a risk-based
 * approach: MLR 2017 reg 18 requires the firm's own assessment, and the MLRO
 * carries the decision, not the software.
 *
 * THE SHAPE OF THE CONTROL.
 *
 * v1_847: every adjustment is the MLRO's, and the role is checked first in
 * both directions. Until v1_847 a raise returned before the role check, so any
 * member (staff and read only included, the route checking firm membership
 * alone) could raise a tier.
 *
 * Raising is the MLRO's, by any amount, with a rationale. Caution needs no
 * further gatekeeping.
 *
 * Lowering is where the risk sits, so it is constrained four ways:
 *   1. MLRO only. Reducing due diligence is the MLRO's call.
 *   2. One band at a time. High to Low in a single step is not a judgement, it
 *      is a disagreement with the whole assessment, which belongs in the firm's
 *      configuration rather than in one case.
 *   3. Never below a legal floor. Where a mandatory override or a gate has
 *      fired, the floor is statutory and no officer may go under it.
 *   4. Never without a written rationale.
 *
 * THE ENGINE TIER IS NEVER OVERWRITTEN. The adjustment records an effective
 * tier alongside it. A reviewer must be able to see that the engine said one
 * thing and a named officer decided another, on a date, for a stated reason.
 * Erasing the engine's answer would be erasing recorded judgement, and it is
 * exactly what a regulator looks for.
 */

import { tierInForce, assessmentPendingReason } from './tier-in-force.js';

const RANK = { Low: 1, Medium: 2, High: 3 };
const BY_RANK = { 1: 'Low', 2: 'Medium', 3: 'High' };

export const TIERS = ['Low', 'Medium', 'High'];

/**
 * The lowest tier this case may be taken to, whatever an officer judges.
 * Derived from the engine's own record of what fired, never from the tier.
 */
export function floorFor(assessment) {
  const a = assessment || {};
  let floor = 'Low';
  let source = null;

  const gateFired = !!a.gate || ((a.rulesEvaluated && a.rulesEvaluated.gates) || []).some(g => g && g.fired);
  if (gateFired) return { tier: 'High', source: 'A gate has fired on this case, which refers it to the MLRO at High and admits no reduction.' };

  for (const o of (a.mandatoryOverrides || [])) {
    if (!o || !o.tier) continue;
    if ((RANK[o.tier] || 0) > (RANK[floor] || 0)) {
      floor = o.tier;
      source = o.label ? o.label + (o.citation ? ' (' + o.citation + ')' : '') : 'A mandatory override has fired.';
    }
  }
  if (a.eddRequired && (RANK[floor] || 0) < RANK.High) {
    floor = 'High';
    source = source || 'Enhanced due diligence is required on this case.';
  }
  return { tier: floor, source };
}

/**
 * Validate a proposed adjustment.
 *
 * @param p.engineTier   the tier the engine produced
 * @param p.assessment   the recorded assessment, for the floor
 * @param p.currentTier  the tier currently in effect (engine, or a prior adjustment)
 * @param p.toTier       the tier the officer wants
 * @param p.isMlro       whether the actor holds the MLRO role
 * @param p.rationale    the officer's written reason
 * @returns { ok, error, direction, floor }
 */
export function validateAdjustment(p = {}) {
  const { engineTier, assessment, isMlro, rationale } = p;
  const current = p.currentTier || engineTier;
  const to = p.toTier;
  const floor = floorFor(assessment);

  if (!TIERS.includes(to)) return { ok: false, error: 'Choose a risk tier of Low, Medium or High.', floor };
  if (!TIERS.includes(current)) return { ok: false, error: 'This case has no assessed tier to adjust.', floor };
  if (to === current) return { ok: false, error: 'That is already the tier in effect on this case.', floor };

  const text = String(rationale || '').trim();
  if (text.length < 20) {
    return { ok: false, error: 'Record why you are changing the tier. The reason is part of the decision and is shown to any reviewer.', floor };
  }

  const direction = (RANK[to] > RANK[current]) ? 'raise' : 'lower';

  // v1_847: the role is checked first, in both directions.
  if (!isMlro) {
    return { ok: false, error: 'Only the MLRO can change a risk tier. Ask the MLRO to review it.', floor, direction };
  }

  // Raising: the MLRO's, any distance. Caution needs no further gatekeeping.
  if (direction === 'raise') return { ok: true, direction, floor };

  if (RANK[current] - RANK[to] > 1) {
    return { ok: false, error: 'A tier can be lowered one band at a time. Lower it to ' + BY_RANK[RANK[current] - 1] + ' first, and again afterwards if that is still your judgement.', floor, direction };
  }
  if (RANK[to] < RANK[floor.tier]) {
    return {
      ok: false,
      direction,
      floor,
      error: 'This case cannot go below ' + floor.tier + '.' + (floor.source ? ' ' + floor.source : '') +
             ' That floor is set by law rather than by the scoring, so it is not open to adjustment.'
    };
  }
  return { ok: true, direction, floor };
}

/** The record written to the file. The engine's answer is kept, never replaced. */
export function buildAdjustment(p = {}) {
  return {
    engineTier: p.engineTier,
    effectiveTier: p.toTier,
    from: p.currentTier || p.engineTier,
    direction: (RANK[p.toTier] > RANK[p.currentTier || p.engineTier]) ? 'raise' : 'lower',
    rationale: String(p.rationale || '').trim(),
    by: p.by || null,
    byRole: p.isMlro ? 'MLRO' : 'Officer',
    at: new Date().toISOString(),
    floorAtTimeOfAdjustment: floorFor(p.assessment).tier,
    methodologyVersion: (p.assessment && p.assessment.methodologyVersion) || null
  };
}

/** The case note. Written so a reviewer reading only the notes sees the whole act. */
export function adjustmentNote(adj) {
  if (!adj) return null;
  return 'Risk tier ' + (adj.direction === 'raise' ? 'raised' : 'lowered') + ' from ' + adj.from + ' to ' +
    adj.effectiveTier + ' by ' + (adj.by || 'an officer') + ' (' + adj.byRole + '). ' +
    'The risk methodology assessed this case as ' + adj.engineTier + '; that assessment stands on the file unchanged. ' +
    'Reason given: ' + adj.rationale;
}

/**
 * ONE FIXED ASSERTION ABOUT THE TIER, used by every surface that reports it
 * (v1_389): the review report, the second sign-off report, the Audit Record and
 * the decision snapshot.
 *
 * It is a single function because the risk with an adjustment is not that it is
 * recorded wrongly, it is that it is recorded in one place and not another. An
 * adjustment visible on screen but absent from the exported record is worse
 * than no adjustment control at all: the export becomes a document that omits
 * the most contestable decision on the file. So every surface renders THIS, and
 * none of them may omit it or condition it on anything.
 */
export function tierStatement(report) {
  const r = report || {};
  const adj = r.tierAdjustment && r.tierAdjustment.effectiveTier ? r.tierAdjustment : null;
  const mov = r.tierMovement && r.tierMovement.to ? r.tierMovement : null;
  // Model tier pending (29/10/2026): the tier in force, never report.riskTier
  // as the model wrote it. None in force reads as Assessment pending.
  const assessed = (r.riskAssessment && r.riskAssessment.tier) || (adj && adj.engineTier) || null;
  const effective = tierInForce(r);
  const method = (r.riskAssessment && r.riskAssessment.methodologyVersion) || (adj && adj.methodologyVersion) || 'CID-RM-1.0';

  const parts = [];
  if (!effective) {
    const why = assessmentPendingReason(r);
    parts.push('No risk tier has been assessed on this case. Assessment pending: the risk assessment has not completed' + (why ? ' (' + why + ')' : '') + ', so no tier is in force.' +
      (r.aiRiskTier ? ' The document analysis suggested ' + r.aiRiskTier + '; that suggestion is not governing.' : ''));
  } else if (adj) {
    const when = adj.at ? new Date(adj.at).toLocaleDateString('en-GB') : 'an unrecorded date';
    parts.push(
      'This case is being run at ' + effective + '. The risk methodology (' + method + ') assessed it as ' +
      adj.engineTier + '. The tier was ' + (adj.direction === 'raise' ? 'raised' : 'lowered') + ' by ' +
      (adj.by || 'an officer') + ' (' + adj.byRole + ') on ' + when + '. Reason given: ' + adj.rationale +
      ' The methodology assessment of ' + adj.engineTier + ' stands on the file unchanged and is not superseded by this adjustment.'
    );
  } else {
    parts.push('Risk tier ' + effective + ', assessed under ' + method + ' with no officer adjustment.');
  }

  if (mov) {
    parts.push(
      'The tier moved from ' + mov.from + ' to ' + mov.to + ' after the case was assembled, because of ' + mov.trigger + '.' +
      (mov.askScalingStale
        ? ' The client request had already been scaled to ' + (mov.askWasScaledTo || mov.from) + ' before the tier moved.'
        : '')
    );
  }

  if (r.interviewScope && r.interviewScope.mode === 'brief') {
    parts.push('The client interview ran in short form on the basis of the low tier. Purpose, intended use and the ownership position were confirmed; enhanced questioning was not required at this tier.');
  }

  return {
    assessedTier: assessed,
    effectiveTier: effective,
    adjusted: !!adj,
    moved: !!mov,
    text: parts.join(' ')
  };
}
